import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection, AnimatedHeading } from "@/components/ui/AnimatedSection";
import { Leaf, MapPin } from "lucide-react";
import regionalesRegalImg from "@/assets/regionales-regal-3.webp";
import weinRegionDetailImg from "@/assets/wein-region-detail.webp";
import partnerPaulsBauernhof from "@/assets/partner-pauls-bauernhof.webp";
import partnerSchreibers from "@/assets/partner-schreibers.webp";
import partnerObsthofScherer from "@/assets/partner-obsthof-scherer.webp";
import partnerFlickWeine from "@/assets/partner-flick-weine.webp";
import partnerGemuesebauHoehl from "@/assets/partner-gemuesebau-hoehl.webp";
import { SEO } from "@/components/SEO";

const partners = [
  {
    name: "Paul's Bauernhof",
    description: "Frische Produkte direkt vom Bauernhof aus der Region.",
    url: "https://www.pauls-bauernhof.de/",
    category: "Bauernhof",
    logo: partnerPaulsBauernhof,
    width: 366,
    height: 144,
  },
  {
    name: "Weingut Schreiber",
    description: "Qualitätsweine aus traditionellem Anbau.",
    url: "https://weingut-schreiber.de/",
    category: "Wein",
    logo: partnerSchreibers,
    width: 600,
    height: 98,
  },
  {
    name: "Flick Weine",
    description: "Erlesene Weine aus der Umgebung.",
    url: "https://shop.flick-wein.de/",
    category: "Wein",
    logo: partnerFlickWeine,
    width: 430,
    height: 600,
  },
  {
    name: "Obsthof Martin Scherer",
    description: "Frisches Obst und regionale Spezialitäten.",
    url: "https://hessische-direktvermarkter.de/betriebe/obsthof-martin-scherer/",
    category: "Obst",
    logo: partnerObsthofScherer,
    width: 600,
    height: 395,
  },
  {
    name: "Gemüsebau Höhl",
    description: "Frisches Gemüse aus regionalem Anbau.",
    url: "https://www.instagram.com/gemuesebau_hoehl/",
    category: "Gemüse",
    logo: partnerGemuesebauHoehl,
    width: 92,
    height: 62,
  },
];

export default function Regionales() {
  return (
    <Layout>
      <SEO title="Regionales | REWE V. Gahndi Flörsheim" description="Regionale Erzeuger und lokale Partner – frische Qualität aus der Region bei REWE V. Gahndi." path="/regionales" />
      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${regionalesRegalImg})` }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative container-custom py-20">
          <AnimatedHeading as="h1" subtitle="Regionales">
            Qualität aus der <span className="text-gold-gradient">Region</span>
          </AnimatedHeading>
          <AnimatedSection>
            <p className="text-center text-muted-foreground max-w-3xl mx-auto text-lg">
              Wir setzen auf regionale Partnerschaften und unterstützen lokale Erzeuger. 
              So bringen wir dir frische Qualität aus der Umgebung direkt in den Markt.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Philosophy with Image */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-2xl aspect-square"
              >
                <img 
                  src={weinRegionDetailImg} 
                  alt="Regionale Weine im REWE Markt" 
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="text-center lg:text-left">
                <Leaf className="w-12 h-12 text-primary mb-6 mx-auto lg:mx-0" />
                <h2 className="text-2xl md:text-3xl font-heading font-semibold mb-6">
                  Unsere <span className="text-gold-gradient">Philosophie</span>
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Bei REWE Gahndi arbeiten wir eng mit regionalen Partnern zusammen und verkaufen 
                  deren hochwertige Produkte. Wir glauben daran, dass kurze Wege nicht nur die 
                  Frische garantieren, sondern auch die lokale Wirtschaft stärken und die Umwelt 
                  schonen.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Durch unsere Zusammenarbeit mit ortsansässigen Bauern und Erzeugern ist unser 
                  Produktangebot immer saisonal geprägt. Du bekommst bei uns das Beste, was die 
                  Region gerade zu bieten hat.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="section-padding bg-secondary">
        <div className="container-custom">
          <AnimatedHeading subtitle="Unsere Partner">
            Lokale <span className="text-gold-gradient">Erzeuger</span>
          </AnimatedHeading>

          <div className="grid md:grid-cols-2 gap-6">
            {partners.map((partner, index) => (
              <AnimatedSection key={partner.name} delay={index * 0.1}>
                <a
                  href={partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card-premium group block hover:border-primary/50 transition-all"
                  aria-label={`${partner.name} besuchen (öffnet in neuem Tab)`}
                >
                  <div>
                    <div className="flex items-center justify-center mb-4 h-20 bg-white rounded-lg p-3">
                      <img 
                        src={partner.logo} 
                        alt={partner.name}
                        width={partner.width}
                        height={partner.height}
                        loading="lazy"
                        className="max-h-14 max-w-full w-auto object-contain"
                      />
                    </div>
                    <span className="text-xs uppercase tracking-wider text-primary mb-2 block text-center">
                      {partner.category}
                    </span>
                    <h3 className="text-xl font-heading font-semibold mb-2 group-hover:text-primary transition-colors text-center">
                      {partner.name}
                    </h3>
                    <p className="text-muted-foreground text-sm text-center">{partner.description}</p>
                  </div>
                </a>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding">
        <div className="container-custom">
          <AnimatedHeading subtitle="Deine Vorteile">
            Warum <span className="text-gold-gradient">regional?</span>
          </AnimatedHeading>

          <div className="grid md:grid-cols-3 gap-6">
            <AnimatedSection>
              <div className="card-premium text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <Leaf className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-heading font-semibold mb-3">Frische garantiert</h3>
                <p className="text-muted-foreground text-sm">
                  Kurze Transportwege bedeuten maximale Frische und besten Geschmack.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              <div className="card-premium text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <MapPin className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-heading font-semibold mb-3">Lokale Wirtschaft</h3>
                <p className="text-muted-foreground text-sm">
                  Mit deinem Einkauf unterstützt du Landwirte und Betriebe aus der Region.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="card-premium text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-semibold mb-3">Umweltschutz</h3>
                <p className="text-muted-foreground text-sm">
                  Weniger Transport bedeutet weniger CO₂ – gut für unseren Planeten.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </Layout>
  );
}
